import { ApolloClient } from '@apollo/client';
import { ComponentProps, forwardRef, useEffect, useState } from 'react';

import { avivaTheme } from '@oui/app-core/src/avivaTheme';
import { AppContainer } from '@oui/app-core/src/components/AppContainer';
import { FLAGS } from '@oui/app-core/src/constants';
import { initApollo } from '@oui/app-core/src/lib/initApollo';
import Sentry from '@oui/app-core/src/sentry';
import { ThemeProvider } from '@oui/app-core/src/styles';
import { CORE_DEEPLINK_CONFIG, DeeplinkConfigShape } from '@oui/app-core/src/types/navigation';
import { StaticTabParamList } from '@oui/app-static/src/types/navigation';

import FullLogo from '@src/assets/Full_logo.svg';
import { RootNavigator } from '@src/RootNavigator';
import { HomeTabParamList, RootStackParamList } from '@src/types/navigation';

import { OnboardingGraphic } from './components/OnboardingGraphic';

const getMessages: ComponentProps<typeof AppContainer>['getMessages'] = ({ lang }) => {
  switch (lang) {
    case 'en': {
      return require('./messages/compiled/en.json');
    }
    default:
      return {};
  }
};

const homeConfig = {
  Home: 'home',
  Learn: 'learn',
  MyPlan: 'myplan',
  Account: { screens: { AccountScreen: 'account' } },
} as const satisfies DeeplinkConfigShape<keyof HomeTabParamList>;
const homeStaticConfig = {
  Learn: 'static/learn',
  Practice: 'static/practice',
  Aid: 'static/aid',
  Profile: 'static/account',
} as const satisfies DeeplinkConfigShape<keyof StaticTabParamList>;
export const DEEPLINK_CONFIG = {
  home: { screens: homeConfig },
  homeStatic: { screens: homeStaticConfig },
  Welcome: 'welcome',
  Connected: 'auth/support',
  Confidentiality: 'Confidentiality',
  CreateTestUser: 'CreateTestUser',
  StaticReview: 'StaticReview',
  StaticSession: 'StaticSession',
  ...CORE_DEEPLINK_CONFIG,
  // @ts-expect-error This doesn't actually exist since AccountSettings is omited in favor of Account
  AccountSettings: '',
} as const satisfies DeeplinkConfigShape<keyof RootStackParamList>;

type Props = {};
export default forwardRef<AppContainer, Props>(function App({}: Props, ref) {
  const [apollo, setApollo] = useState<ApolloClient<unknown>>();

  useEffect(() => {
    // This will only run in development when fast-refresh kicks in
    let cleanupRef = { current: () => {} };

    initApollo((cb) => {
      cleanupRef.current = cb;
    })
      .then(setApollo)
      .catch(Sentry.captureException);

    return () => cleanupRef.current();
  }, []);

  return apollo ? (
    <ThemeProvider theme={avivaTheme}>
      <AppContainer
        Logo={FullLogo}
        flags={FLAGS}
        app={() => <RootNavigator />}
        apollo={apollo}
        ref={ref}
        initialPath={({
          user,
        }): Parameters<GeneratedClientRoutes.GetDeeplinkPath['avivaSupport']>[0] => {
          if (!user) return '/welcome';
          if (user.role?.productStatic) {
            return '/static/learn';
          }
          return '/home';
        }}
        deeplinkConfig={{
          screens: DEEPLINK_CONFIG,
        }}
        getMessages={getMessages}
        onboardingGraphic={<OnboardingGraphic />}
      />
    </ThemeProvider>
  ) : null;
});
